import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

interface CatNode {
  id: string | number;
  name: string;
  task?: { id: any; name: string; active: any }[];
  children?: CatNode[];
}

interface TaskOption {
  value: number;
  label: string;
}

interface Props {
  nodes: CatNode[];
  selectedCategoryId: number[];
  categoryName: any;
  onCategorySelect: (id: number, name: string) => void;
  selectedTasks: TaskOption[];
  setSelectedTasks: (tasks: TaskOption[]) => void;
  taskOrTasks: boolean;
  formError?: boolean;
  onErrorClear?: () => void;
  /** Только выбор категории — без панели задач (выбор категории сразу закрывает dropdown) */
  categoryOnly?: boolean;
  /** Триггер во всю ширину контейнера (для вертикальных форм вроде finance-панелей) */
  block?: boolean;
  /** Разрешить выбор узла-группы (с детьми), а не только листа — раскрытие через шеврон. Для таксономий-папок (Documents). */
  allowGroupSelect?: boolean;
}

const ACCENT = '#2563eb';

function hexToRgba(hex: string, a: number) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
}

/* Find path from root to a node by id, returns array of names */
function findPath(nodes: CatNode[], targetId: string | number, path: string[] = []): string[] | null {
  for (const n of nodes) {
    const cur = [...path, n.name];
    if (String(n.id) === String(targetId)) return cur;
    if (n.children) {
      const found = findPath(n.children, targetId, cur);
      if (found) return found;
    }
  }
  return null;
}

/* Find node by id */
function findNode(nodes: CatNode[], targetId: string | number): CatNode | null {
  for (const n of nodes) {
    if (String(n.id) === String(targetId)) return n;
    if (n.children) { const f = findNode(n.children, targetId); if (f) return f; }
  }
  return null;
}


const CategoryTaskPicker: React.FC<Props> = ({
  nodes,
  selectedCategoryId,
  onCategorySelect,
  selectedTasks,
  setSelectedTasks,
  taskOrTasks,
  formError,
  onErrorClear,
  categoryOnly = false,
  block = false,
  allowGroupSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeCatId, setActiveCatId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 350 });

  /* Sync from external selectedCategoryId */
  useEffect(() => {
    if (selectedCategoryId.length > 0) {
      const id = String(selectedCategoryId[0]);
      setActiveCatId(id);
      /* auto-expand parent groups */
      const path = findPath(nodes, id);
      if (path && path.length > 1) {
        // expand all ancestors
        const ids = new Set<string>();
        const expand = (ns: CatNode[], target: string, acc: string[]): boolean => {
          for (const n of ns) {
            if (String(n.id) === target) return true;
            if (n.children) {
              if (expand(n.children, target, [...acc, String(n.id)])) {
                acc.forEach(aid => ids.add(aid));
                ids.add(String(n.id));
                return true;
              }
            }
          }
          return false;
        }
        expand(nodes, id, []);
        setExpandedIds(ids);
      }
    } else {
      setActiveCatId(null);
    }
  }, [selectedCategoryId, nodes]);

  /* Fetch tasks when activeCatId changes */
  useEffect(() => {
    if (categoryOnly || !activeCatId) { setTasks([]); return; }
    setLoadingTasks(true);
    axios.get(`category/${activeCatId}`)
      .then(({ data }) => {
        setTasks((data.task || [])
          .filter((t: any) => t.active)
          .map((t: any) => ({ value: t.id, label: t.name })));
      })
      .catch(() => setTasks([]))
      .finally(() => setLoadingTasks(false));
  }, [activeCatId, categoryOnly]);

  /* Close on outside click — учитываем Portal dropdown через dropRef */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const inTrigger = wrapRef.current?.contains(e.target as Node);
      const inDrop    = dropRef.current?.contains(e.target as Node);
      if (!inTrigger && !inDrop) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const toggleGroup = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectCat = useCallback((node: CatNode) => {
    if (node.children?.length && !allowGroupSelect) { toggleGroup(String(node.id)); return; }
    const id = String(node.id);
    setActiveCatId(id);
    setSelectedTasks([]);
    onCategorySelect(Number(id), node.name);
    onErrorClear?.();
    if (categoryOnly) setIsOpen(false);
  }, [toggleGroup, onCategorySelect, setSelectedTasks, onErrorClear, categoryOnly, allowGroupSelect]);

  const openDropdown = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropW = block ? Math.max(rect.width, categoryOnly ? 240 : 350) : 350;
      const left = rect.left + dropW > window.innerWidth ? rect.right - dropW : rect.left;
      setDropPos({ top: rect.bottom + 4, left, width: dropW });
    }
    setIsOpen(o => !o);
    onErrorClear?.();
  }, [onErrorClear, block, categoryOnly]);

  // Закрывать при ресайзе окна (позиция могла сдвинуться)
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [isOpen]);

  const selectTask = useCallback((task: TaskOption) => {
    if (taskOrTasks) {
      setSelectedTasks(
        selectedTasks.some(t => t.value === task.value)
          ? selectedTasks.filter(t => t.value !== task.value)
          : [...selectedTasks, task]
      );
    } else {
      setSelectedTasks([task]);
      setIsOpen(false);
    }
  }, [taskOrTasks, selectedTasks, setSelectedTasks]);

  /* Derived display values */
  const pathParts = activeCatId ? findPath(nodes, activeCatId) : null;
  const selectedTaskLabels = selectedTasks.map(t => t.label);
  const activeCatNode = activeCatId ? findNode(nodes, activeCatId) : null;

  /* ── RENDER TREE RECURSIVELY ── */
  const renderTree = (list: CatNode[], depth = 0): React.ReactNode => {
    return [...list].sort((a, b) => a.name.localeCompare(b.name)).map((node, idx) => {
      const isLeaf = !node.children?.length;
      const id = String(node.id);
      const isExpanded = expandedIds.has(id);
      const isSelected = activeCatId === id;

      if (!isLeaf) {
        const hasSel = !!activeCatId && !!findNode(node.children!, activeCatId);
        return (
          <div key={id}>
            {/* Group header */}
            <div
              onClick={() => (allowGroupSelect ? selectCat(node) : toggleGroup(id))}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: `${depth > 0 ? 5 : 6}px 8px`,
                borderRadius: 7, cursor: 'pointer', transition: 'background .11s',
                background: isSelected ? hexToRgba(ACCENT, .09) : (isExpanded ? 'var(--btn-user-form)' : 'transparent'),
                paddingLeft: 8 + depth * 12,
                position: 'relative',
              }}
              onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--btn-user-form)'; }}
              onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = isExpanded ? 'var(--btn-user-form)' : 'transparent'; }}
            >
              {/* left accent bar when group has selection */}
              {(hasSel || isSelected) && (
                <div style={{ position: 'absolute', left: 0, top: '20%', height: '60%', width: 3, borderRadius: '0 2px 2px 0', background: ACCENT }} />
              )}
              <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--text-color)', lineHeight: 1.3 }}>{node.name}</span>
              <span
                onClick={allowGroupSelect ? (e => { e.stopPropagation(); toggleGroup(id); }) : undefined}
                style={{ fontSize: 8, color: 'var(--text-color-calendar-week)', transition: 'transform .2s', transform: isExpanded ? 'rotate(90deg)' : 'none', flexShrink: 0, cursor: 'pointer', padding: allowGroupSelect ? '4px 6px' : 0, margin: allowGroupSelect ? '-4px -2px' : 0 }}
              >›</span>
            </div>
            {/* Children (collapsible) */}
            <div style={{
              overflow: 'hidden',
              maxHeight: isExpanded ? 999 : 0,
              transition: 'max-height .22s cubic-bezier(.4,0,.2,1)',
              paddingLeft: 10,
            }}>
              {isExpanded && renderTree(node.children!, depth + 1)}
            </div>
          </div>
        );
      }

      /* Leaf node */
      return (
        <div
          key={id}
          onClick={() => selectCat(node)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '6px 8px', borderRadius: 7, cursor: 'pointer',
            transition: 'background .11s',
            paddingLeft: 8 + depth * 12,
            position: 'relative',
            background: isSelected ? hexToRgba(ACCENT, .09) : 'transparent',
          }}
          onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--btn-user-form)'; }}
          onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          {isSelected && (
            <div style={{ position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)', width: 2, height: '70%', background: ACCENT, borderRadius: 1 }} />
          )}
          <span style={{ flex: 1, fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? 'var(--text-color)' : 'var(--text-color-calendar)', lineHeight: 1.3 }}>
            {node.name}
          </span>
          {!categoryOnly && (
            <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>
              {(node.task || []).filter((t: any) => t.active).length}
            </span>
          )}
        </div>
      );
    });
  };

  /* ── TRIGGER CONTENT ── */
  const triggerContent = () => {
    if (!activeCatId) {
      return <span style={{ color: 'var(--text-color-calendar-week)', fontWeight: 400 }}>{categoryOnly ? 'Select category…' : 'Select category & task…'}</span>;
    }
    const catLabel = activeCatNode?.name || '';
    const parent = pathParts && pathParts.length > 1 ? pathParts.slice(0, -1).join(' / ') : null;
    if (!selectedTasks.length) {
      return (
        <>
          {parent && <span style={{ color: 'var(--text-color-calendar-week)', fontWeight: 400 }}>{parent}&nbsp;/&nbsp;</span>}
          <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>{catLabel}</span>
        </>
      );
    }
    const taskStr = selectedTaskLabels.length === 1
      ? selectedTaskLabels[0]
      : `${selectedTaskLabels[0]} +${selectedTaskLabels.length - 1}`;
    return (
      <>
        {parent && <span style={{ color: 'var(--text-color-calendar-week)', fontWeight: 400, whiteSpace: 'nowrap' }}>{parent}&nbsp;/&nbsp;</span>}
        <span style={{ color: 'var(--text-color)', fontWeight: 600, whiteSpace: 'nowrap' }}>{catLabel}</span>
        <span style={{ color: 'var(--text-color-calendar-week)', margin: '0 4px', flexShrink: 0 }}>·</span>
        <span style={{ color: 'var(--text-color)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{taskStr}</span>
      </>
    );
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0, ...(block ? { width: '100%' } : {}) }}>
      {/* TRIGGER */}
      <div
        ref={triggerRef}
        className="edit-input rounded-[8px] cursor-pointer"
        onClick={openDropdown}
        style={{
          height: block ? 40 : 32, padding: '0', fontSize: block ? 14 : 12, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 0,
          ...(block ? { width: '100%', minWidth: 0, maxWidth: 'none' } : { minWidth: 200, maxWidth: 320 }),
          overflow: 'hidden',
          ...(formError ? { borderColor: '#dc2626', background: 'rgba(220,38,38,.06)' } : {}),
          ...(isOpen ? { borderColor: hexToRgba(ACCENT, .4), boxShadow: `0 0 0 3px ${hexToRgba(ACCENT, .07)}` } : {}),
        }}
      >
        {/* Color stripe */}
        <div style={{
          width: activeCatId ? 3 : 0, height: '100%', background: ACCENT, flexShrink: 0,
          transition: 'width .2s cubic-bezier(.4,0,.2,1)',
        }} />
        {/* Text path */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden', padding: '0 8px', gap: 0, minWidth: 0 }}>
          {triggerContent()}
        </div>
        {/* Clear or arrow */}
        {activeCatId ? (
          <span
            style={{ padding: '0 8px', color: 'var(--text-color-calendar-week)', cursor: 'pointer', fontSize: 11, flexShrink: 0, lineHeight: 1 }}
            onClick={e => {
              e.stopPropagation();
              setActiveCatId(null);
              setTasks([]);
              setSelectedTasks([]);
              setIsOpen(false);
              onCategorySelect(-1, '');
            }}
          >✕</span>
        ) : (
          <span style={{ padding: '0 8px', fontSize: 10, color: 'var(--text-color-calendar-week)', opacity: .6, flexShrink: 0, transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
        )}
      </div>

      {/* DROPDOWN — Portal в body, вне любых stacking context от backdrop-filter */}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropRef}
          style={{
            position: 'fixed',
            top: dropPos.top,
            left: dropPos.left,
            zIndex: 99999,
            display: 'flex', overflow: 'hidden',
            background: 'var(--bg-form)',
            border: '1px solid var(--border-input)',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,.3)',
            minWidth: dropPos.width,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          {/* LEFT: category tree */}
          <div style={{ width: categoryOnly ? '100%' : '50%', borderRight: categoryOnly ? 'none' : '1px solid var(--border-input)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ padding: '8px 12px 6px', fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', flexShrink: 0 }}>
              Category
            </div>
            <div style={{ overflowY: 'auto', maxHeight: 288, padding: 5, scrollbarWidth: 'thin', scrollbarColor: 'var(--border-input) transparent' }}>
              {renderTree(nodes)}
            </div>
          </div>

          {/* RIGHT: tasks */}
          {!categoryOnly && (
          <div style={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
            {/* breadcrumb */}
            <div style={{ padding: '8px 12px 6px', borderBottom: '1px solid var(--border-input)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, minHeight: 33 }}>
              {activeCatId && pathParts ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-color-calendar-week)', overflow: 'hidden' }}>
                  {pathParts.map((seg, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span style={{ fontSize: 9, flexShrink: 0 }}>›</span>}
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: i === pathParts.length - 1 ? 700 : 400, color: i === pathParts.length - 1 ? 'var(--text-color)' : 'var(--text-color-calendar-week)' }}>
                        {seg}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>Tasks</span>
              )}
            </div>
            {/* task list */}
            <div style={{ overflowY: 'auto', maxHeight: 288, padding: 5, scrollbarWidth: 'thin', scrollbarColor: 'var(--border-input) transparent' }}>
              {!activeCatId && (
                <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 12, color: 'var(--text-color-calendar-week)', lineHeight: 1.5 }}>
                  ← Select a category
                </div>
              )}
              {activeCatId && loadingTasks && (
                <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 11, color: 'var(--text-color-calendar-week)' }}>Loading…</div>
              )}
              {activeCatId && !loadingTasks && tasks.length === 0 && (
                <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 11, color: 'var(--text-color-calendar-week)' }}>No tasks</div>
              )}
              {[...tasks].sort((a, b) => a.label.localeCompare(b.label)).map(task => {
                const isSel = selectedTasks.some(t => t.value === task.value);
                return (
                  <div
                    key={task.value}
                    onClick={() => selectTask(task)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9, padding: '7px 9px',
                      borderRadius: 7, cursor: 'pointer', transition: 'background .11s',
                      background: isSel ? hexToRgba(ACCENT, .09) : 'transparent',
                    }}
                    onMouseEnter={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'var(--btn-user-form)'; }}
                    onMouseLeave={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <span style={{ flex: 1, fontSize: 13, fontWeight: isSel ? 700 : 400, color: isSel ? 'var(--text-color)' : 'var(--text-color-calendar)' }}>
                      {task.label}
                    </span>
                    {isSel && <span style={{ fontSize: 11, color: '#059669', flexShrink: 0 }}>✓</span>}
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CategoryTaskPicker;
